import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const KEY = "fah_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.product_id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.product_id === product.id ? { ...p, qty: p.qty + qty } : p
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          mrp: product.mrp,
          image: product.images?.[0] || "",
          unit: product.unit,
          seller_name: product.seller_name,
          qty,
        },
      ];
    });
    toast.success(`Added "${product.name}" to cart`);
  };

  // Upsert for "Buy Now" — no duplicates. Adds if missing, sets qty if present.
  // Writes localStorage SYNCHRONOUSLY so a subsequent navigate("/checkout") sees the item.
  const upsertItem = (product, qty = 1) => {
    const newItem = {
      product_id: product.id,
      name: product.name,
      price: product.price,
      mrp: product.mrp,
      image: product.images?.[0] || "",
      unit: product.unit,
      seller_name: product.seller_name,
      qty,
    };
    setItems((prev) => {
      const existing = prev.find((p) => p.product_id === product.id);
      const next = existing
        ? prev.map((p) => (p.product_id === product.id ? { ...p, qty } : p))
        : [...prev, newItem];
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) return removeItem(productId);
    setItems((prev) => prev.map((p) => (p.product_id === productId ? { ...p, qty } : p)));
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((p) => p.product_id !== productId));
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, p) => s + p.price * p.qty, 0);
  const count = items.reduce((s, p) => s + p.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, upsertItem, updateQty, removeItem, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
